import { RootState } from '@/models/root-state.model';

import { createSelector } from 'reselect';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { FluidBonusData } from '@fluidpayments/types';
import { bonusFluidImage } from '@/utils/image-utils';
import { selectUserTags } from '@/store/selectors/userSelectors';
import { isEmpty } from '@/shared/utility';
import {
  BffBonusData,
  BffBonusType,
  BffUserTagsResponse,
  CmsAfterFlowModalTrigger,
  GigBonusRedeemType,
} from '@lucky7ventures/bff-types';

const filterBonusesWithTags = (
  includedTags: [] | string[],
  excludedTags: [] | string[],
  userTags: BffUserTagsResponse,
) => {
  if (userTags && userTags.length > 0 && !isEmpty(includedTags)) {
    return userTags.some(tag => includedTags.includes(tag.name as never));
  }

  if (userTags && userTags.length > 0 && !isEmpty(excludedTags)) {
    return !userTags.some(tag => excludedTags.includes(tag.name as never));
  }

  return true;
};

export const selectAllAvailableBonuses = (state: RootState): BffBonusData[] | [] =>
  state.bonuses[BffBonusType.Available];
export const selectALLActiveBonuses = (state: RootState): BffBonusData[] | [] =>
  state.bonuses[BffBonusType.Active];
export const selectAvailableBonuses = createSelector(
  selectAllAvailableBonuses,
  selectUserTags,
  (allAvailableBonuses, tags) => {
    return allAvailableBonuses.filter(({ bonusIncludedUserTags, bonusExcludedUserTags }) =>
      filterBonusesWithTags(bonusIncludedUserTags, bonusExcludedUserTags, tags),
    );
  },
);

export const selectActiveBonuses = createSelector(
  selectALLActiveBonuses,
  selectUserTags,
  (allActiveBonuses, tags) =>
    allActiveBonuses.filter(({ bonusIncludedUserTags, bonusExcludedUserTags }) =>
      filterBonusesWithTags(bonusIncludedUserTags, bonusExcludedUserTags, tags),
    ),
);

export const selectBonusesLoading = (state: RootState): boolean => state.bonuses.loading;

export const defineMaxBonusPerLocale = (maxBonusAmount: number | null, locale: string): number => {
  /*
   * GiG sends max bonus amount based in Euro currency, which is
   * inaccurate for Mexico, Norway, South Africa and Thailand.
   * Hence we need to do manual multiplier conversion from Euro
   * to those countries' currencies:
   * MXN = multiplier by 20
   * NOK = multiplier by 10
   * ZAR  = multiplier by 16
   * THB = multiplier by 35
   * */

  if (!maxBonusAmount) return 0;

  switch (locale) {
    case 'mx':
      return maxBonusAmount * 20;
    case 'no':
      return maxBonusAmount * 10;
    case 'za':
      return maxBonusAmount * 16;
    case 'th':
      return maxBonusAmount * 35;
    default:
      return maxBonusAmount;
  }
};

export const selectFluidCashierBonuses = createSelector(
  selectAvailableBonuses,
  selectLocale,
  (bonuses, locale): FluidBonusData => {
    return bonuses
      .filter(bonus => bonus.redeemTypeId !== GigBonusRedeemType.BonusCode && bonus.promoCode) // There was a bug in Fluid that if we pass a bonus that doesn't have a promo code, the fluid cashier would crash
      .map(bonus => {
        return {
          code: bonus.promoCode!,
          selected: false,
          title: bonus.title,
          description: bonus.heading ?? '',
          logoUrl: bonusFluidImage(bonus.image ?? ''),
          maxBonusPercentage: bonus.bonusPercentage ?? undefined,
          maxBonus: defineMaxBonusPerLocale(bonus.maxBonusAmount, locale),
          minDeposit: defineMaxBonusPerLocale(bonus.minDepositAmount ?? 0, locale),
          paymentMethodFilter: {
            // We have a fixed list of excluded payment methods for each bonus
            // Todo: excluded payment methods list should be fetched for together with bonus content from WP
            excludeFrom: [
              {
                providerType: 'NETELLER',
              },
              {
                providerType: 'SKRILL',
              },
              {
                providerType: 'PAYSAFECARD',
              },
              {
                providerType: 'ECOPAYZ',
              },
            ],
          },
        };
      });
  },
);

export const selectLoginBonuses = createSelector(selectAvailableBonuses, availableBonuses =>
  availableBonuses.filter(
    bonus =>
      bonus.afterFlowModalTrigger !== null &&
      bonus.afterFlowModalTrigger.includes(CmsAfterFlowModalTrigger.Login),
  ),
);

export const selectWelcomeOfferBonuses = createSelector(
  selectAvailableBonuses,
  availableBonuses => {
    return availableBonuses.filter(
      bonus =>
        bonus.afterFlowModalTrigger !== null &&
        (bonus.afterFlowModalTrigger.includes(CmsAfterFlowModalTrigger.Registration) ||
          bonus.afterFlowModalTrigger.includes(CmsAfterFlowModalTrigger.Login)),
    );
  },
);

export const selectHasWelcomeBonuses = createSelector(selectAvailableBonuses, bonuses => {
  if (bonuses.length === 0) return false;
  return bonuses.some(bonus => {
    if (bonus.afterFlowModalTrigger === null) {
      return false;
    }
    return bonus.afterFlowModalTrigger.some(
      flow =>
        flow === CmsAfterFlowModalTrigger.Registration || flow === CmsAfterFlowModalTrigger.Login,
    );
  });
});

export const selectBonusesTurnedOverPercentage = createSelector(selectActiveBonuses, bonuses => {
  if (bonuses.length === 0) return null;

  let leftToTurnover = 0;
  let totalTurnover = 0;

  bonuses.forEach(bonus => {
    leftToTurnover += bonus.wageringAmount ?? 0;
    totalTurnover += bonus.bonusWagering ?? 0;
  });

  if (totalTurnover === 0) return null;

  const staked = totalTurnover - leftToTurnover;

  return Math.floor((staked / totalTurnover) * 100);
});
